import { getSession, identityFor } from '@bloo-team/core-shell'
import type { Identity } from '@bloo-team/core-shell'

/**
 * Who reaches which Case settings tab (docs/cases.md §Settings). The
 * case-management service answers its settings to admins only: a Security
 * Engineer gets Statuses & transitions, ITSM and Grouping; every Root user —
 * any role, TenantAdmin or not, 'No Access' included — gets Evidence and
 * Features. A Root Security Engineer gets all five. A UX affordance, as all
 * of core-shell's permissions are: the service still decides.
 */

export const CASE_SETTINGS_TABS = ['statuses', 'itsm', 'grouping', 'evidence', 'features'] as const
export type CaseSettingsTab = (typeof CASE_SETTINGS_TABS)[number]

const ENGINEER_TABS: readonly CaseSettingsTab[] = ['statuses', 'itsm', 'grouping']
const ROOT_TABS: readonly CaseSettingsTab[] = ['evidence', 'features']

const isEngineer = (id: Identity) => id.role === 'Security Engineer'
const isRoot = (id: Identity) => id.userType === 'Root'

/** The tabs this identity sees, in page order; none when it has no grant. */
export function caseSettingsTabsFor(id: Identity | null): CaseSettingsTab[] {
  if (!id) return []
  return CASE_SETTINGS_TABS.filter((t) => (isEngineer(id) && ENGINEER_TABS.includes(t)) || (isRoot(id) && ROOT_TABS.includes(t)))
}

/** The settings nav gate for Vantage › Cases (with `admitsNoAccess`, so a 'Root - No Access' identity passes). */
export const caseSettingsGate = (id: Identity) => caseSettingsTabsFor(id).length > 0

/** This session's tabs in a cluster and scope. */
export function caseSettingsTabsAt(clusterId: string, scopeId: string): CaseSettingsTab[] {
  const session = getSession()
  return caseSettingsTabsFor(session ? identityFor(session, clusterId, scopeId) : null)
}

/** The tab shown for `wanted`: itself when this identity sees it, else its first tab; null with none. */
export function resolveCaseSettingsTab(id: Identity | null, wanted: CaseSettingsTab | undefined): CaseSettingsTab | null {
  const tabs = caseSettingsTabsFor(id)
  return wanted && tabs.includes(wanted) ? wanted : (tabs[0] ?? null)
}
